import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import SpeczeLogo from '../../assets/SpeczeLogo'
import { Alert } from '../../components/ui'
import toast from 'react-hot-toast'

function OAuthButton({ provider, icon, label }) {
  return (
    <a href={`/api/auth/${provider}`}
      className="flex items-center justify-center gap-2.5 w-full py-2.5 rounded-xl border border-white/12 bg-white/4 hover:bg-white/8 hover:border-white/20 text-sm font-medium text-white/80 hover:text-white transition-all duration-200">
      {icon}
      {label}
    </a>
  )
}

export default function Login() {
  const [form, setForm]     = useState({ email: '', password: '' })
  const [error, setError]   = useState('')
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()
  const navigate  = useNavigate()

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const handleSubmit = async e => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(form.email, form.password)
      toast.success('Welcome back!')
      navigate('/dashboard')
    } catch (err) {
      setError(err.response?.data?.errors?.email?.[0] || err.response?.data?.message || 'Invalid credentials.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #07020f 0%, #180a3a 40%, #0d1a5c 70%, #07020f 100%)' }}>

      {/* Stars */}
      <div className="absolute inset-0 stars-bg opacity-60" />
      {/* Orbs */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full blur-3xl pointer-events-none bg-purple-600/15" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full blur-3xl pointer-events-none bg-blue-600/10" />

      <div className="relative z-10 w-full max-w-[420px] mx-auto px-6 py-12">

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5">
            <SpeczeLogo size={44} />
          </div>
          <h1 className="font-display font-bold text-2xl text-white">Welcome back</h1>
          <p className="text-white/40 text-sm mt-1">Sign in to your Specze account</p>
        </div>

        {/* Card */}
        <div className="glass-card p-7">
          {error && <div className="mb-5"><Alert type="error">{error}</Alert></div>}

          {/* OAuth */}
          <div className="space-y-2.5 mb-6">
            <OAuthButton provider="github" label="Continue with GitHub"
              icon={<svg className="w-4 h-4 fill-white" viewBox="0 0 24 24"><path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/></svg>}
            />
            <OAuthButton provider="gitlab" label="Continue with GitLab"
              icon={<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 01-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 014.82 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0118.6 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.51L23 13.45a.84.84 0 01-.35.94z" fill="#FC6D26"/></svg>}
            />
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-white/8" />
            <span className="text-xs text-white/30 font-medium">or continue with email</span>
            <div className="flex-1 h-px bg-white/8" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label-specze">Email address</label>
              <input className="input-specze" type="email" required autoFocus autoComplete="email"
                value={form.email} onChange={set('email')} placeholder="you@example.com" />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="label-specze mb-0">Password</label>
                <Link to="/forgot-password" className="text-[11px] text-purple-400 hover:text-purple-300 transition-colors">
                  Forgot password?
                </Link>
              </div>
              <input className="input-specze" type="password" required autoComplete="current-password"
                value={form.password} onChange={set('password')} placeholder="••••••••••" />
            </div>
            <button type="submit" disabled={loading} className="btn-specze w-full py-3 text-white mt-2"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)', boxShadow: '0 0 20px rgba(139,63,255,0.4)' }}>
              {loading
                ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Signing in…</>
                : 'Sign in to Specze'}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-white/40 mt-6">
          New to Specze?{' '}
          <Link to="/register" className="text-purple-400 hover:text-purple-300 font-semibold transition-colors">
            Start free trial
          </Link>
        </p>
      </div>
    </div>
  )
}
